import MarkdownIt from 'markdown-it';
import { generateJSON, generateHTML } from '@tiptap/html';
import { StarterKit } from '@tiptap/starter-kit';
import { Link, isAllowedUri } from '@tiptap/extension-link';
import { Image } from '@tiptap/extension-image';
import { Highlight } from '@tiptap/extension-highlight';
import { CodeBlock } from '@tiptap/extension-code-block';
import { Table, TableRow, TableCell, TableHeader } from '@tiptap/extension-table';
import { Subscript } from '@tiptap/extension-subscript';
import { Superscript } from '@tiptap/extension-superscript';
import { TaskList } from '@tiptap/extension-task-list';
import { TaskItem } from '@tiptap/extension-task-item';
import { ApiEndpoint } from './apiEndpoint';
import { ApiSpec } from './apiSpec';
import { Callout } from './callout';

// Code block with a line-number gutter in the editor. renderHTML stays the
// standard <pre><code class="language-x"> (so migration import + Shiki render on
// the site are unaffected); the NodeView only runs in the browser editor.
const CodeBlockWithLineNumbers = CodeBlock.extend({
  addNodeView() {
    return ({ node }) => {
      const dom = document.createElement('div');
      dom.className = 'cb';
      const gutter = document.createElement('div');
      gutter.className = 'cb-gutter';
      gutter.contentEditable = 'false';
      const pre = document.createElement('pre');
      const code = document.createElement('code');
      if (node.attrs.language) code.className = `language-${node.attrs.language}`;
      pre.appendChild(code);
      dom.append(gutter, pre);
      const paint = (n: any) => {
        const lines = Math.max(1, (n.textContent.match(/\n/g)?.length ?? 0) + 1);
        gutter.textContent = Array.from({ length: lines }, (_, i) => String(i + 1)).join('\n');
      };
      paint(node);
      return {
        dom,
        contentDOM: code,
        update: (updated: any) => {
          if (updated.type.name !== node.type.name) return false;
          code.className = updated.attrs.language ? `language-${updated.attrs.language}` : '';
          paint(updated);
          return true;
        },
      };
    };
  },
});

// Link with author-controlled tab behaviour. StarterKit's built-in Link forces
// target="_blank" on EVERY link (its default HTMLAttributes + the target
// attribute's own default), so imported internal cross-links wrongly opened a
// new tab. Here the `target` attribute defaults to null (= "no explicit choice"),
// and the author's per-link choice is stored as target='_blank' (new tab) or
// '_self' (same tab). renderHTML honours an explicit choice; when none was made
// (old imported content, target=null) it defaults by href: external → new tab,
// internal/relative → same tab. rel is only emitted for new-tab links.
const DocLink = Link.extend({
  addAttributes() {
    return { ...this.parent?.(), target: { default: null }, rel: { default: null } };
  },
  renderHTML({ HTMLAttributes }) {
    const attrs: Record<string, any> = { ...HTMLAttributes };
    const href = typeof attrs.href === 'string' ? attrs.href : '';
    // Re-check the scheme at render (upstream's terminal safety net): a href that
    // slipped past parse-time validation is blanked rather than emitted. (M7)
    if (href && !isAllowedUri(href, this.options.protocols)) attrs.href = '';
    const external = /^(https?:)?\/\//i.test(href) || /^(mailto:|tel:)/i.test(href);
    if (attrs.target == null && external) attrs.target = '_blank'; // sensible default for legacy content
    if (attrs.target === '_blank') attrs.rel = attrs.rel || 'noopener noreferrer nofollow';
    else { delete attrs.target; delete attrs.rel; } // same tab → clean <a href>
    return ['a', attrs, 0];
  },
});

// Image with an optional caption. When a caption is set it renders as a
// <figure><img><figcaption>…</figcaption></figure>; otherwise a bare <img>. The
// caption is a node attribute (set from the CMS editor), and figure/img HTML is
// parsed back into it so pasted/imported captioned images round-trip.
const CaptionedImage = Image.extend({
  addAttributes() {
    return { ...this.parent?.(), caption: { default: null } };
  },
  parseHTML() {
    return [
      {
        tag: 'figure',
        getAttrs: (el) => {
          const fig = el as HTMLElement;
          const img = fig.querySelector('img');
          if (!img) return false;
          const cap = fig.querySelector('figcaption');
          return {
            src: img.getAttribute('src'),
            alt: img.getAttribute('alt'),
            title: img.getAttribute('title'),
            caption: (cap?.textContent ?? '').trim() || null,
          };
        },
      },
      { tag: 'img[src]' },
    ];
  },
  renderHTML({ HTMLAttributes }) {
    const { caption, ...img } = HTMLAttributes as Record<string, any>;
    return caption
      ? ['figure', { class: 'doc-figure' }, ['img', img], ['figcaption', {}, caption]]
      : ['img', img];
  },
});

// Table with an author-controlled "searchable" flag. When on, the docs site shows
// a row-filter search box above the table; the flag renders as data-searchable so
// the SSR renderer and the editor both see it. null = unset (renderer falls back to
// a >8-row heuristic for legacy/imported tables). Columns are resizable.
const DocTable = Table.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      searchable: {
        default: null,
        parseHTML: (el) => {
          const v = (el as HTMLElement).getAttribute('data-searchable');
          return v === null ? null : v === 'true';
        },
        renderHTML: (attrs) =>
          attrs.searchable === null || attrs.searchable === undefined
            ? {}
            : { 'data-searchable': String(attrs.searchable) },
      },
    };
  },
}).configure({ resizable: true });

// Shared TipTap schema + (de)serialisation for the docs module. The SAME
// extension set is used by the migration importer, the SSR docs renderer, and
// (later) the admin WYSIWYG editor — so migrated, edited, and rendered content
// all agree on the node/mark vocabulary. Canonical doc content is TipTap JSON.
export const docExtensions = [
  StarterKit.configure({ codeBlock: false, link: false }),
  DocLink.configure({ openOnClick: false, autolink: true }),
  CodeBlockWithLineNumbers,
  CaptionedImage,
  Highlight.configure({ multicolor: true }),
  Subscript,
  Superscript,
  TaskList,
  TaskItem.configure({ nested: true }),
  DocTable,
  TableRow,
  TableCell,
  TableHeader,
  Callout,
  ApiEndpoint,
  ApiSpec,
];

const md = new MarkdownIt({ html: true, linkify: true, breaks: false });

/** Normalise GitBook-flavoured Markdown into Markdown/HTML the extensions parse. */
export function normalizeGitbookMd(src: string): string {
  let s = src;
  s = s.replace(/^>\s*For the complete documentation index[\s\S]*?\)\.\s*$/m, '');
  s = s.replace(
    /<figure>\s*<img([^>]*?)\/?>\s*(?:<figcaption>([\s\S]*?)<\/figcaption>)?\s*<\/figure>/gi,
    (_m, attrs: string, cap = '') => {
      const src2 = (attrs.match(/src="([^"]*)"/i) ?? [])[1] ?? '';
      const alt = (attrs.match(/alt="([^"]*)"/i) ?? [])[1] ?? '';
      const capText = String(cap).replace(/<[^>]+>/g, '').trim();
      return `\n\n<img src="${src2}" alt="${alt}">\n\n${capText ? `*${capText}*\n\n` : ''}`;
    },
  );
  s = s.replace(/{%\s*hint[^%]*%}([\s\S]*?){%\s*endhint\s*%}/gi, (_m, body: string) =>
    `\n\n> ${String(body).trim().replace(/\n+/g, '\n> ')}\n\n`);
  s = s.replace(/{%\s*code[^%]*%}([\s\S]*?){%\s*endcode\s*%}/gi, (_m, body: string) =>
    `\n\n\`\`\`\n${String(body).trim()}\n\`\`\`\n\n`);
  s = s.replace(/{%\s*content-ref\s+url="([^"]*)"\s*%}([\s\S]*?){%\s*endcontent-ref\s*%}/gi,
    (_m, u: string, body: string) => `\n\n[${String(body).replace(/<[^>]+>/g, '').trim() || u}](${u})\n\n`);
  s = s.replace(/{%[^%]*%}/g, '');
  s = s.replace(/&#x20;?/g, ' ').replace(/&#x26;/g, '&');
  s = s.replace(/<(https?:\/\/[^>\s]+)\s*>/g, '$1');
  // GitBook exports internal doc links as absolute own-host URLs ending in `.md`
  // (e.g. https://help.cymmetri.io/getting-started/release-notes/3.1.0.md). Rewrite
  // them to relative rendered-page links (strip our host + the `.md`) so the import
  // itself produces working same-tab internal links — no separate post-fix pass.
  // Only matches links whose path ends in `.md`, so asset/image URLs are untouched.
  s = s.replace(
    /\]\(\s*(?:https?:\/\/(?:help|docs)\.cymmetri\.io)?(\/[^)\s]*?)\.md((?:[#?][^)\s]*)?)\s*\)/gi,
    ']($1$2)',
  );
  return s;
}

/** GitBook Markdown → TipTap/ProseMirror JSON. */
export function mdToTiptapJSON(src: string): any {
  return generateJSON(md.render(normalizeGitbookMd(src)), docExtensions);
}

/** HTML → TipTap/ProseMirror JSON (e.g. from a Word/.docx conversion). */
export function htmlToTiptapJSON(html: string): any {
  return generateJSON(html, docExtensions);
}

/** TipTap JSON → HTML. */
export function tiptapToHTML(doc: any): string {
  if (!doc || typeof doc !== 'object') return '';
  return generateHTML(doc, docExtensions);
}

/** First H1 of the Markdown = page title. */
export function extractTitle(src: string): string {
  const m = src.match(/^#\s+(.+)$/m);
  return m ? m[1]!.trim() : '';
}

export function slugifyHeading(text: string): string {
  return text.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 64) || 'section';
}

export interface TocItem { level: number; text: string; id: string }

/** Plain text of a TipTap node subtree. */
function nodeText(node: any): string {
  if (!node) return '';
  if (node.type === 'text') return node.text ?? '';
  return (node.content ?? []).map(nodeText).join('');
}

/** Extract H2/H3 headings (for the "on this page" TOC), with unique slug ids. */
export function extractToc(doc: any): TocItem[] {
  const out: TocItem[] = [];
  const seen = new Map<string, number>();
  for (const node of doc?.content ?? []) {
    if (node.type !== 'heading') continue;
    const level = node.attrs?.level ?? 1;
    if (level < 2 || level > 3) continue;
    const text = nodeText(node).trim();
    if (!text) continue;
    let id = slugifyHeading(text);
    const n = seen.get(id) ?? 0;
    seen.set(id, n + 1);
    if (n) id = `${id}-${n}`;
    out.push({ level, text, id });
  }
  return out;
}

/** Render a doc: HTML (with heading ids injected, in order) + its TOC. */
export function renderDoc(doc: any): { html: string; toc: TocItem[] } {
  const toc = extractToc(doc);
  let html = tiptapToHTML(doc);
  // Inject ids into h2/h3 sequentially, matching TOC order.
  const ids = [...toc];
  html = html.replace(/<h([23])>/g, (m, lvl) => {
    const next = ids.shift();
    return next && String(next.level) === String(lvl) ? `<h${lvl} id="${next.id}">` : m;
  });
  // Wrap each table in a horizontally-scrollable container, and show the row-filter
  // search box when the author enabled it (data-searchable="true"); unset tables
  // fall back to a >8-row heuristic, and data-searchable="false" disables it.
  html = html.replace(/<table([^>]*)>([\s\S]*?)<\/table>/g, (_full, attrs: string, inner: string) => {
    const explicit = /data-searchable="true"/.test(attrs) ? true : /data-searchable="false"/.test(attrs) ? false : null;
    const rows = (inner.match(/<tr\b/g) ?? []).length;
    const showSearch = explicit === null ? rows > 8 : explicit;
    const search = showSearch
      ? '<input class="table-search" type="search" placeholder="Search this table…" aria-label="Search table"/>'
      : '';
    return `<div class="table-wrap">${search}<table${attrs}>${inner}</table></div>`;
  });
  return { html, toc };
}
